import { useMemo, type JSX, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { extractRawRoles, resolveAdminRoles, type AdminRoleConfig } from './admin-role';
import { AdminRoleContext, type AdminRoleState } from './admin-role-context';

interface AuthConfigResponse {
  rolesClaim: string;
  adminRoles: { admin: string; aiAdmin: string; auditor: string };
}

export function AdminRoleProvider({ children }: { children: ReactNode }): JSX.Element {
  const { accounts } = useMsal();

  const config = useQuery({
    queryKey: ['/api/auth/config'],
    queryFn: () => api.get<AuthConfigResponse>('/api/auth/config'),
    staleTime: Infinity,
  });

  // Behind auth (EW-65), so it needs a token and cannot be folded into the query above.
  const needsFallback = accounts.length > 0;
  const fallback = useQuery({
    queryKey: ['/api/auth/config/admin-role-fallback'],
    queryFn: () => api.get<{ adminRoleFallback: boolean }>('/api/auth/config/admin-role-fallback'),
    enabled: needsFallback,
    staleTime: Infinity,
  });

  const value = useMemo<AdminRoleState>(() => {
    if (config.isPending || (needsFallback && fallback.isPending)) {
      return { roles: [], isPending: true, isError: false };
    }
    if (config.isError || !config.data) return { roles: [], isPending: false, isError: true };

    const cfg: AdminRoleConfig = {
      rolesClaim: config.data.rolesClaim,
      roleNames: config.data.adminRoles,
      // A failed fallback query is default deny, not a broken portal — the claim roles still hold.
      fallback: fallback.data?.adminRoleFallback ?? false,
    };
    const claims = accounts[0]?.idTokenClaims;
    return {
      roles: resolveAdminRoles(extractRawRoles(claims, cfg.rolesClaim), cfg),
      isPending: false,
      isError: false,
    };
  }, [
    accounts,
    config.isPending,
    config.isError,
    config.data,
    needsFallback,
    fallback.isPending,
    fallback.data,
  ]);

  return <AdminRoleContext.Provider value={value}>{children}</AdminRoleContext.Provider>;
}
